import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import { listBuilds } from "@/api/builds";
import { RefreshFailed, loadFailure } from "@/components/Spinner";
import { Button, EmptyState, Page, PageHeader, StatusPill, Table, type TableColumn } from "@/components/ui";
import { formatDateTime } from "@/lib/dates";
import type { BuildChannel, DataplaneBuild } from "@/types/api";

// Dataplane repo — commit ids link here. Kept as a constant (the catalogue
// is single-repo) rather than threaded through runtime config.
const DATAPLANE_REPO_URL = "https://github.com/pulsestation/pulsestation-dataplane";

// A channel is a label, not a state: no dot.
function ChannelBadge({ channel }: { channel: BuildChannel }) {
  return (
    <StatusPill tone={channel === "production" ? "success" : "warning"} dot={false}>
      {channel}
    </StatusPill>
  );
}

function shortKey(value: string | undefined, n = 12): string {
  if (!value) return "—";
  return value.length > n ? value.slice(0, n) : value;
}

const CHANNEL_FILTERS: { label: string; value: BuildChannel | "all" }[] = [
  { label: "All", value: "all" },
  { label: "Production", value: "production" },
  { label: "Beta", value: "beta" },
];

const COLUMNS: TableColumn<DataplaneBuild>[] = [
  {
    key: "build",
    header: "Build",
    mono: true,
    cell: (build) => (
      <a
        href={`${DATAPLANE_REPO_URL}/commit/${build.commitSha}`}
        target="_blank"
        rel="noreferrer"
        className="text-brand-600 hover:underline"
      >
        {build.buildId}
      </a>
    ),
  },
  { key: "channel", header: "Channel", cell: (build) => <ChannelBadge channel={build.channel} /> },
  { key: "branch", header: "Branch", mono: true, cell: (build) => build.branch },
  { key: "rust", header: "Rust", mono: true, muted: true, cell: (build) => shortKey(build.components?.rust?.version) },
  { key: "gui", header: "GUI", mono: true, muted: true, cell: (build) => shortKey(build.components?.gui?.version, 16) },
  { key: "pslib", header: "pslib", mono: true, muted: true, cell: (build) => build.components?.pslib?.version ?? "—" },
  { key: "built", header: "Built", mono: true, muted: true, cell: (build) => formatDateTime(build.builtAt) },
];

export function BuildsPage() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const [filter, setFilter] = useState<BuildChannel | "all">("all");

  const query = useQuery({
    queryKey: ["builds", filter],
    queryFn: () => listBuilds(token, filter === "all" ? undefined : filter),
    enabled: !!token,
  });

  return (
    <Page>
      <PageHeader
        title="Builds"
        subtitle={
          <>
            One build per dataplane commit. <strong>master</strong> →{" "}
            <em>production</em>, every other branch → <em>beta</em>. Pick a build
            from the deploy dialog on a stage; production stages accept
            production builds only.
          </>
        }
        actions={
          <div className="flex gap-1">
            {CHANNEL_FILTERS.map((c) => (
              <Button
                key={c.value}
                variant={filter === c.value ? "primary" : "ghost"}
                aria-pressed={filter === c.value}
                onClick={() => setFilter(c.value)}
              >
                {c.label}
              </Button>
            ))}
          </div>
        }
      />

      <RefreshFailed query={query} className="mb-4" />
      <Table
        label="Builds"
        columns={COLUMNS}
        rows={query.data?.items}
        rowKey={(build) => build.buildId}
        error={loadFailure(query)}
        empty={
          filter === "all" ? (
            <EmptyState title="No builds registered yet">
              Push to the dataplane repo to populate the catalogue (register-build.yml).
            </EmptyState>
          ) : (
            <EmptyState title="No builds in this channel" />
          )
        }
      />
    </Page>
  );
}
